import { useMemo, type ReactNode } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';
import type { Components } from 'react-markdown';
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react';
import { Markdown, childrenToText } from '../components/ui/markdown';
import { ThemeToggle } from '../components/ui/theme-toggle';
import {
  DOC_GROUPS, docBySlug, docsInGroup, headingId, headingsOf, visibleDocs, type DocEntry,
} from '../docs';
import { Diagram } from '../docs/diagrams';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/utils';

/**
 * The documentation at `/doc`.
 *
 * Its own shell rather than the app `Layout`, for two reasons. It carries no session: these are
 * instructions about the product, shareable with someone who has not signed in, and the app sidebar
 * is a hierarchy of a *particular* organization's projects — meaningless here. And it needs
 * page-level scrolling so in-page anchors work natively, which the console's fixed-sidebar shell
 * (every page its own scroll container) does not give.
 *
 * Content lives in `src/docs` as Markdown. See the note there and in CLAUDE.md: this surface is
 * curated by explicit request, not filled in opportunistically.
 */

/**
 * Docs-scale typography, merged over the shared Markdown renderer.
 *
 * The defaults are tuned for KB pages and run drawers — panes that can be dragged down to a few
 * hundred pixels — so headings there are deliberately small. A document read as a document wants
 * more contrast between its levels than that.
 *
 * `h2` also carries the anchor id the section nav links to, derived through the same `headingId` the
 * nav uses, so the two cannot drift. `scroll-mt` keeps a targeted heading clear of the viewport
 * edge.
 */
/**
 * The diagram a fenced block names, or null for an ordinary code block.
 *
 * A ```` ```diagram ```` fence holds one line: the key into the diagram registry. Reading it off the
 * `<code>` element's props rather than re-parsing the markdown keeps one parser in play, and keeps
 * the convention where the fence is — `headingsOf` already skips fenced blocks, so a diagram can
 * never be mistaken for a section.
 */
function diagramName(children: ReactNode): string | null {
  const node = Array.isArray(children) ? children[0] : children;
  if (!node || typeof node !== 'object' || !('props' in node)) return null;
  const props = (node as { props: { className?: string; children?: ReactNode } }).props;
  if (!/(?:^|\s)language-diagram(?:\s|$)/.test(props.className ?? '')) return null;
  return childrenToText(props.children).trim() || null;
}

const docComponents: Components = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold text-ink leading-tight mb-4">{children}</h1>
  ),

  /**
   * Fenced blocks, with one exception.
   *
   * `pre` rather than `code` is where this is intercepted, because returning the diagram from here
   * means the `<code>` child is never rendered — so there is no wrapper to unstyle and no chance of
   * the registry key appearing as text behind the picture.
   *
   * Only this surface has it. `docComponents` is merged over the shared renderer, which every other
   * surface uses unchanged: a KB body, a run's output or an automation's document that happened to
   * contain a `diagram` fence renders as the code block it is.
   */
  pre: ({ children }) => {
    const name = diagramName(children);
    if (name) return <Diagram name={name} />;
    return (
      <pre className="bg-surface-subtle border border-border-subtle rounded-md p-3 mb-4 overflow-x-auto">
        {children}
      </pre>
    );
  },
  h2: ({ children }) => (
    <h2
      id={headingId(childrenToText(children))}
      className="scroll-mt-6 text-lg font-bold text-ink mt-10 mb-3 pb-1.5 border-b border-border-subtle"
    >
      {children}
    </h2>
  ),
  h3: ({ children }) => <h3 className="text-sm font-semibold text-ink mt-6 mb-2">{children}</h3>,

  /**
   * A link to another page of the documentation stays in the app; anything else keeps the shared
   * renderer's outward behaviour (new tab, `noreferrer`).
   *
   * Overridden here rather than in the shared renderer because the default is right for its usual
   * subject — links inside *ingested* content, which point outward and are not ours to trust. These
   * guides cross-reference each other heavily, and a full page load per cross-reference would throw
   * away the app shell to go one document sideways. In-page anchors (`#section`) fall through to the
   * plain `<a>`, which is what makes the browser scroll rather than route.
   */
  a: ({ children, href }) => {
    if (href?.startsWith('/')) {
      return (
        <Link to={href} className="text-orange-600 dark:text-orange-400 hover:underline">
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-orange-600 dark:text-orange-400 hover:underline break-words"
      >
        {children}
      </a>
    );
  },
  p: ({ children }) => <p className="text-sm text-ink-secondary leading-7 mb-4">{children}</p>,
  li: ({ children }) => <li className="text-sm text-ink-secondary leading-7">{children}</li>,
  ul: ({ children }) => <ul className="list-disc ml-5 mb-4 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ml-5 mb-4 space-y-1">{children}</ol>,
};

function DocNav({ active, docs }: { active: DocEntry | null; docs: DocEntry[] }) {
  const headings = useMemo(() => (active ? headingsOf(active.content) : []), [active]);

  return (
    <nav className="space-y-6">
      {DOC_GROUPS.map((group) => {
        const entries = docsInGroup(group.group, docs);
        // A heading over an empty list reads as a loading failure, and a group can legitimately be
        // empty — a new one exists before the pages that go in it.
        if (entries.length === 0) return null;

        return (
          <div key={group.group}>
            <p className="px-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {entries.map((doc) => {
                const isActive = active?.slug === doc.slug;
                return (
                  <div key={doc.slug}>
                    <NavLink
                      to={`/doc/${doc.slug}`}
                      className={cn(
                        'block px-2 py-1.5 rounded-md text-xs font-medium transition-colors',
                        isActive
                          ? 'bg-orange-50 dark:bg-orange-950/40 text-orange-600 dark:text-orange-400'
                          : 'text-ink-muted hover:bg-surface-subtle hover:text-ink',
                      )}
                    >
                      {doc.title}
                    </NavLink>

                    {/* The open document's own sections, nested under it — one nav rather than a
                        separate table of contents, since they are the same hierarchy. */}
                    {isActive && headings.length > 0 && (
                      <div className="mt-0.5 mb-1 ml-2 pl-2 border-l border-border-subtle space-y-0.5">
                        {headings.map((heading) => (
                          <a
                            key={heading.id}
                            href={`#${heading.id}`}
                            className="block px-2 py-1 rounded text-[11px] text-ink-faint hover:text-ink hover:bg-surface-subtle transition-colors"
                          >
                            {heading.text}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </nav>
  );
}

/** The `/doc` landing page: every document with its one-line summary. */
function DocIndex({ docs }: { docs: DocEntry[] }) {
  return (
    <div>
      <h1 className="text-2xl font-bold text-ink">Documentation</h1>
      <p className="mt-2 text-sm text-ink-muted leading-relaxed max-w-2xl">
        How to use CDR Memory: setting up projects, writing and reviewing knowledge, and putting
        agents to work on it.
      </p>

      <div className="mt-8 space-y-8">
        {DOC_GROUPS.map((group) => {
          const entries = docsInGroup(group.group, docs);
          if (entries.length === 0) return null;

          return (
            <section key={group.group}>
              <h2 className="text-sm font-semibold text-ink">{group.label}</h2>
              <p className="mt-0.5 text-xs text-ink-faint">{group.description}</p>

              <div className="mt-3 border border-border rounded-lg overflow-hidden divide-y divide-border-subtle bg-card">
                {entries.map((doc) => (
                  <Link
                    key={doc.slug}
                    to={`/doc/${doc.slug}`}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-surface-subtle transition-colors group"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-ink">{doc.title}</p>
                      <p className="mt-0.5 text-xs text-ink-muted leading-relaxed">{doc.blurb}</p>
                    </div>
                    <ArrowRight
                      size={14}
                      className="mt-0.5 flex-shrink-0 text-ink-faint group-hover:text-orange-500 transition-colors"
                    />
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

/** Next/previous across the whole document list, so a guide can be read straight through. */
function DocPager({ doc, docs }: { doc: DocEntry; docs: DocEntry[] }) {
  const index = docs.findIndex((d) => d.slug === doc.slug);
  const previous = index > 0 ? docs[index - 1] : null;
  const next = index < docs.length - 1 ? docs[index + 1] : null;

  if (!previous && !next) return null;

  return (
    <div className="mt-12 pt-5 border-t border-border flex items-stretch gap-3">
      {previous && (
        <Link
          to={`/doc/${previous.slug}`}
          className="flex-1 min-w-0 px-4 py-3 rounded-lg border border-border bg-card hover:border-orange-300 transition-colors"
        >
          <span className="block text-[10px] uppercase tracking-wide text-ink-faint">Previous</span>
          <span className="block mt-0.5 text-xs font-medium text-ink truncate">{previous.title}</span>
        </Link>
      )}
      {next && (
        <Link
          to={`/doc/${next.slug}`}
          className="flex-1 min-w-0 px-4 py-3 rounded-lg border border-border bg-card hover:border-orange-300 transition-colors text-right"
        >
          <span className="block text-[10px] uppercase tracking-wide text-ink-faint">Next</span>
          <span className="block mt-0.5 text-xs font-medium text-ink truncate">{next.title}</span>
        </Link>
      )}
    </div>
  );
}

function NotFound() {
  return (
    <div className="border border-dashed border-border rounded-lg py-14 px-6 text-center">
      <p className="text-sm text-ink-secondary mb-1">There is no such document</p>
      <p className="text-xs text-ink-faint mb-4">
        It may have been renamed, or never existed. The list on the left is everything there is.
      </p>
      <Link to="/doc" className="text-xs text-orange-500 hover:text-orange-700 transition-colors">
        Back to the index
      </Link>
    </div>
  );
}

export default function Docs() {
  const { slug } = useParams<{ slug: string }>();
  // The guides follow the reader's feature flags: a guide to a section they cannot open is left out
  // everywhere, and its URL reads as not found. A signed-out reader has every flag on.
  const { hasFeature } = useAuth();
  const docs = useMemo(() => visibleDocs(hasFeature), [hasFeature]);
  const found = docBySlug(slug);
  const doc = found && docs.includes(found) ? found : null;

  return (
    <div className="min-h-screen bg-surface">
      <div className="mx-auto max-w-6xl px-6 lg:px-8">
        <header className="flex items-center gap-3 py-4 border-b border-border">
          <Link to="/doc" className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">C</span>
            </div>
            <div className="min-w-0">
              <div className="text-sm font-bold text-ink leading-tight truncate">CDR Memory</div>
              <div className="text-[10px] text-ink-faint uppercase tracking-wide">Documentation</div>
            </div>
          </Link>

          <div className="ml-auto flex items-center gap-3">
            <ThemeToggle />
            {/* Always offered rather than only when signed in: this shell has no session of its
                own, and an unauthenticated reader following it lands on the login page, which is
                where they were going anyway. */}
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-ink-muted hover:text-ink transition-colors whitespace-nowrap"
            >
              <ArrowLeft size={13} />
              Console
            </Link>
          </div>
        </header>

        <div className="flex items-start gap-10">
          {/* Sticky rather than fixed, so it scrolls the header away with the page and then stays. */}
          <div className="hidden lg:block w-60 flex-shrink-0 sticky top-0 max-h-screen overflow-y-auto py-8">
            <DocNav active={doc} docs={docs} />
          </div>

          <main className="min-w-0 flex-1 max-w-3xl py-8">
            {/* Narrow screens get the document list as a strip instead of a rail — a nav column at
                that width would push the prose into a gutter. */}
            <div className="lg:hidden -mx-6 px-6 pb-5 mb-5 border-b border-border-subtle overflow-x-auto">
              <div className="flex items-center gap-1.5 w-max">
                <NavLink
                  to="/doc"
                  end
                  className={({ isActive }) =>
                    cn(
                      'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border whitespace-nowrap transition-colors',
                      isActive
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-card text-ink-muted border-border',
                    )
                  }
                >
                  <BookOpen size={11} />
                  Index
                </NavLink>
                {docs.map((entry) => (
                  <NavLink
                    key={entry.slug}
                    to={`/doc/${entry.slug}`}
                    className={({ isActive }) =>
                      cn(
                        'px-2.5 py-1 rounded-full text-[11px] font-medium border whitespace-nowrap transition-colors',
                        isActive
                          ? 'bg-orange-500 text-white border-orange-500'
                          : 'bg-card text-ink-muted border-border',
                      )
                    }
                  >
                    {entry.title}
                  </NavLink>
                ))}
              </div>
            </div>

            {slug === undefined ? (
              <DocIndex docs={docs} />
            ) : doc ? (
              <>
                <Markdown components={docComponents}>{doc.content}</Markdown>
                <DocPager doc={doc} docs={docs} />
              </>
            ) : (
              <NotFound />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
